<template>
  <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" @click.self="closeModal">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content" v-if="userDetails">
        <div class="modal-header">
          <h5 class="modal-title">User Performance Details - {{ getStreamName() }}</h5>
          <div class="ms-auto me-3">
            <span class="badge bg-info">{{ getDateRangeLabel() }}</span>
          </div>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- User Info Header -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card bg-primary text-white">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <h3 class="text-white mb-2">{{ userDetails.userInfo.names }}</h3>
                      <p class="mb-1"><i class="bx bx-id-card"></i> ID: {{ userDetails.userInfo.idNo }}</p>
                      <p class="mb-1"><i class="bx bx-map"></i> Zone: {{ userDetails.userInfo.zone }}</p>
                      <p class="mb-0"><i class="bx bx-building"></i> {{ userDetails.userInfo.subCounty }} - {{ userDetails.userInfo.ward }}</p>
                    </div>
                    <div class="col-md-4 text-end">
                      <div class="avatar-lg mx-auto">
                        <div class="avatar-title bg-white text-primary rounded-circle font-size-24">
                          <i class="bx bx-user"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Performance Metrics -->
          <div class="row mb-4">
            <div class="col-md-3">
              <div class="card">
                <div class="card-body">
                  <h6 class="text-muted">Current Period</h6>
                  <h4 class="mb-2">KES {{ numberFormat(userDetails.performanceMetrics.currentMonth.revenue) }}</h4>
                  <p class="mb-0">
                    <span class="badge bg-success">{{ userDetails.performanceMetrics.currentMonth.achievementRate }}%</span>
                    of target
                  </p>
                  <small class="text-muted">{{ userDetails.performanceMetrics.currentMonth.transactions }} transactions</small>
                </div>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card">
                <div class="card-body">
                  <h6 class="text-muted">Previous Period</h6>
                  <h4 class="mb-2">KES {{ numberFormat(userDetails.performanceMetrics.lastMonth.revenue) }}</h4>
                  <p class="mb-0">
                    <span :class="growthClass">
                      <i :class="growthIcon"></i> {{ userDetails.performanceMetrics.lastMonth.growth }}%
                    </span>
                  </p>
                  <small class="text-muted">{{ userDetails.performanceMetrics.lastMonth.transactions }} transactions</small>
                </div>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card">
                <div class="card-body">
                  <h6 class="text-muted">Period Summary</h6>
                  <h4 class="mb-2">KES {{ numberFormat(userDetails.performanceMetrics.yearToDate.revenue) }}</h4>
                  <p class="mb-0">Avg/Day: KES {{ numberFormat(userDetails.performanceMetrics.yearToDate.averagePerDay) }}</p>
                  <small class="text-muted">{{ userDetails.performanceMetrics.yearToDate.transactions }} total</small>
                </div>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card">
                <div class="card-body">
                  <h6 class="text-muted">Best Day</h6>
                  <h4 class="mb-2">KES {{ numberFormat(userDetails.performanceMetrics.yearToDate.bestDay.revenue) }}</h4>
                  <p class="mb-0">{{ formatDate(userDetails.performanceMetrics.yearToDate.bestDay.date) }}</p>
                  <small class="text-muted">Peak performance</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Monthly Trend Chart -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="card-title mb-0">Performance Trend - {{ getStreamName() }}</h5>
                  <p class="text-muted mb-0">{{ getDateRangeLabel() }}</p>
                </div>
                <div class="card-body">
                  <div id="monthlyTrendChart" style="height: 350px;"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Transaction Breakdown -->
          <div class="row mb-4">
            <div class="col-md-6">
              <div class="card">
                <div class="card-header">
                  <h5 class="card-title mb-0">Revenue by Category</h5>
                </div>
                <div class="card-body">
                  <div ref="categoryPieChart" style="height: 300px; min-height: 300px; width: 100%;"></div>
                  <!-- Always show table as alternative view -->
                  <div class="mt-3">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <tbody>
                        <tr v-for="(item, category) in validTransactionBreakdown" :key="category">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="color-box" :style="{ backgroundColor: getCategoryColor(category) }"></div>
                              {{ category || 'Uncategorized' }}
                            </div>
                          </td>
                          <td class="text-end">{{ numberFormat(item.amount) }}</td>
                          <td class="text-end">{{ ((item.amount / totalRevenue) * 100).toFixed(1) }}%</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="card">
                <div class="card-header">
                  <h5 class="card-title mb-0">Transaction Details</h5>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                      <tr>
                        <th>Category</th>
                        <th class="text-end">Amount</th>
                        <th class="text-end">Count</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(item, category) in validTransactionBreakdown" :key="category">
                        <td>{{ category || 'Uncategorized' }}</td>
                        <td class="text-end">{{ numberFormat(item.amount) }}</td>
                        <td class="text-end">{{ item.count }}</td>
                      </tr>
                      </tbody>
                      <tfoot>
                      <tr class="fw-bold">
                        <td>Total</td>
                        <td class="text-end">{{ numberFormat(totalRevenue) }}</td>
                        <td class="text-end">{{ totalTransactions }}</td>
                      </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- User Transactions Section -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0">User Transactions</h5>
                  <div>
                    <button class="btn btn-sm btn-outline-primary me-2" @click="refreshTransactions">
                      <i class="bx bx-refresh"></i> Refresh
                    </button>
                    <button class="btn btn-sm btn-primary" @click="exportTransactions">
                      <i class="bx bx-download"></i> Export
                    </button>
                  </div>
                </div>
                <div class="card-body">
                  <!-- Transaction filters -->
                  <div class="row mb-3">
                    <div class="col-md-4">
                      <input type="date" class="form-control form-control-sm" v-model="transactionFilters.date" @change="filterTransactions">
                    </div>
                    <div class="col-md-4">
                      <select class="form-select form-select-sm" v-model="transactionFilters.category" @change="filterTransactions">
                        <option value="">All Categories</option>
                        <option v-for="category in uniqueCategories" :key="category" :value="category">{{ category }}</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <select class="form-select form-select-sm" v-model="transactionFilters.amountRange" @change="filterTransactions">
                        <option value="">All Amounts</option>
                        <option value="low">{{ 'Low (< KES 500)' }}</option>
                        <option value="medium">Medium (KES 500 - 2000)</option>
                        <option value="high">{{ 'High (> KES 2000)' }}</option>
                      </select>
                    </div>
                  </div>

                  <div class="table-responsive" style="max-height: 400px;">
                    <table class="table table-sm table-hover">
                      <thead class="sticky-top bg-light">
                      <tr>
                        <th>Date/Time</th>
                        <th>Category</th>
                        <th class="text-end">Amount</th>
                        <th>Receipt</th>
                        <th>Zone</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="transaction in paginatedTransactions" :key="transaction.id">
                        <td>
                          <div>
                            <div class="fw-bold">{{ formatDateTime(transaction.date) }}</div>
                            <small class="text-muted">{{ formatTime(transaction.date) }}</small>
                          </div>
                        </td>
                        <td>{{ transaction.category }}</td>
                        <td class="text-end">
                          <span class="fw-bold text-success">KES {{ numberFormat(transaction.amount) }}</span>
                        </td>
                        <td>
                          <span class="badge bg-light text-dark">{{ transaction.receiptNumber }}</span>
                        </td>
                        <td>{{ transaction.zone || 'N/A' }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination for transactions -->
                  <div class="row mt-3" v-if="transactionsTotalPages > 1">
                    <div class="col-12">
                      <nav>
                        <ul class="pagination pagination-sm justify-content-center">
                          <li class="page-item" :class="{ disabled: transactionsCurrentPage === 1 }">
                            <a class="page-link" @click="transactionsCurrentPage = 1" href="javascript:void(0);">First</a>
                          </li>
                          <li class="page-item" :class="{ disabled: transactionsCurrentPage === 1 }">
                            <a class="page-link" @click="transactionsCurrentPage--" href="javascript:void(0);">Previous</a>
                          </li>
                          <li class="page-item" v-for="page in visibleTransactionPages" :key="page"
                              :class="{ active: page === transactionsCurrentPage }">
                            <a class="page-link" @click="transactionsCurrentPage = page" href="javascript:void(0);">{{ page }}</a>
                          </li>
                          <li class="page-item" :class="{ disabled: transactionsCurrentPage === transactionsTotalPages }">
                            <a class="page-link" @click="transactionsCurrentPage++" href="javascript:void(0);">Next</a>
                          </li>
                          <li class="page-item" :class="{ disabled: transactionsCurrentPage === transactionsTotalPages }">
                            <a class="page-link" @click="transactionsCurrentPage = transactionsTotalPages" href="javascript:void(0);">Last</a>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          <button type="button" class="btn btn-primary" @click="exportUserReport">
            <i class="bx bx-download"></i> Export Report
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div class="modal-content" v-else>
        <div class="modal-body text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3">Loading user details...</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrop -->
  <div class="modal-backdrop fade" :class="{ show: show }" v-if="show"></div>
</template>

<script>
/* eslint-disable */
import Highcharts from 'highcharts';
import { execute, biller } from "@/api";

export default {
  name: "UserDetailsModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    userId: {
      type: String,
      default: null
    },
    streamPrefix: {
      type: String,
      default: 'MKT'
    },
    dateRange: {
      type: Object,
      default: () => ({ type: 'this_month', startDate: '', endDate: '' })
    }
  },
  data() {
    return {
      userDetails: null,
      loading: false,
      fetchTimeout: null,
      lastFetchedUserId: null,

      // Transaction management
      allTransactions: [],
      filteredTransactions: [],
      transactionFilters: {
        date: '',
        category: '',
        amountRange: ''
      },
      transactionsCurrentPage: 1,
      transactionsPerPage: 10
    }
  },
  computed: {
    growthClass() {
      if (!this.userDetails) return '';
      const growth = this.userDetails.performanceMetrics.lastMonth.growth;
      return growth > 0 ? 'text-success' : 'text-danger';
    },
    growthIcon() {
      if (!this.userDetails) return '';
      const growth = this.userDetails.performanceMetrics.lastMonth.growth;
      return growth > 0 ? 'bx bx-up-arrow-alt' : 'bx bx-down-arrow-alt';
    },
    validTransactionBreakdown() {
      if (!this.userDetails?.transactionBreakdown) return {};

      // Filter out entries where the item is undefined or has invalid data
      const filtered = {};
      Object.entries(this.userDetails.transactionBreakdown).forEach(([category, item]) => {
        if (item && typeof item.amount === 'number' && typeof item.count === 'number') {
          filtered[category] = item;
        }
      });
      return filtered;
    },
    totalRevenue() {
      if (!this.validTransactionBreakdown) return 0;
      return Object.values(this.validTransactionBreakdown)
          .reduce((sum, item) => sum + (item.amount || 0), 0);
    },
    totalTransactions() {
      if (!this.validTransactionBreakdown) return 0;
      return Object.values(this.validTransactionBreakdown)
          .reduce((sum, item) => sum + (item.count || 0), 0);
    },
    uniqueCategories() {
      return [...new Set(this.allTransactions.map(t => t.category))].filter(Boolean);
    },
    paginatedTransactions() {
      const start = (this.transactionsCurrentPage - 1) * this.transactionsPerPage;
      const end = start + this.transactionsPerPage;
      return this.filteredTransactions.slice(start, end);
    },
    transactionsTotalPages() {
      return Math.ceil(this.filteredTransactions.length / this.transactionsPerPage);
    },
    visibleTransactionPages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.transactionsCurrentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.transactionsTotalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    // Watch for show prop changes
    show: {
      immediate: true,
      handler(newVal, oldVal) {
        console.log('Show prop changed:', { newVal, oldVal, userId: this.userId, streamPrefix: this.streamPrefix });
        if (newVal && this.userId && this.streamPrefix) {
          // Always fetch when modal is shown with a userId and streamPrefix
          this.fetchUserDetails();
        }
      }
    },
    // Watch for userId prop changes
    userId: {
      immediate: false,
      handler(newVal, oldVal) {
        console.log('UserId prop changed:', { newVal, oldVal, show: this.show, streamPrefix: this.streamPrefix });
        // Fetch if modal is shown and userId changed
        if (this.show && newVal && this.streamPrefix) {
          this.fetchUserDetails();
        }
      }
    },
    // Watch for streamPrefix changes
    streamPrefix: {
      immediate: false,
      handler(newVal, oldVal) {
        console.log('StreamPrefix prop changed:', { newVal, oldVal, show: this.show, userId: this.userId });
        if (this.show && this.userId && newVal) {
          this.fetchUserDetails();
        }
      }
    },
    // Watch for dateRange changes
    dateRange: {
      immediate: false,
      handler(newVal, oldVal) {
        console.log('DateRange prop changed:', { newVal, oldVal, show: this.show, userId: this.userId });
        if (this.show && this.userId && this.streamPrefix &&
            (newVal.type !== oldVal?.type || newVal.startDate !== oldVal?.startDate || newVal.endDate !== oldVal?.endDate)) {
          this.fetchUserDetails();
        }
      },
      deep: true
    }
  },
  mounted() {
    console.log('UserDetailsModal mounted with props:', {
      show: this.show,
      userId: this.userId,
      streamPrefix: this.streamPrefix,
      dateRange: this.dateRange
    });
  },
  methods: {
    getStreamName() {
      const streamNames = {
        'MKT': 'Markets',
        'PKN': 'Parking',
        'SBP': 'Single Business Permit',
        'CESS': 'Cess',
        'CLMP': 'Clamping',
        'RNT': 'Housing Rent',
        'ADV': 'Advertisements'
      };
      return streamNames[this.streamPrefix] || this.streamPrefix;
    },

    getDateRangeLabel() {
      switch (this.dateRange.type) {
        case 'today':
          return 'Today';
        case 'yesterday':
          return 'Yesterday';
        case 'this_week':
          return 'This Week';
        case 'this_month':
          return 'This Month';
        case 'custom':
          return `${this.dateRange.startDate} to ${this.dateRange.endDate}`;
        default:
          return 'Current Period';
      }
    },

    async fetchUserDetails() {
      console.log('fetchUserDetails called with:', {
        userId: this.userId,
        streamPrefix: this.streamPrefix,
        dateRange: this.dateRange,
        show: this.show
      });

      if (!this.userId || !this.streamPrefix) {
        console.log('Missing userId or streamPrefix');
        return;
      }

      // Check if we're already fetching this user with this stream and date range
      const fetchKey = `${this.userId}_${this.streamPrefix}_${this.dateRange.type}_${this.dateRange.startDate}_${this.dateRange.endDate}`;
      if (this.loading && this.lastFetchedUserId === fetchKey) {
        console.log('Already fetching this user+stream+daterange, skipping');
        return;
      }

      // Clear any pending timeout
      if (this.fetchTimeout) {
        clearTimeout(this.fetchTimeout);
      }

      // Use a small timeout to debounce rapid calls
      this.fetchTimeout = setTimeout(async () => {
        this.loading = true;
        this.lastFetchedUserId = fetchKey;
        this.userDetails = null; // Clear previous data

        try {
          const data = new FormData();
          data.append("function", "getUserTransactionDetails");
          data.append("incomeTypePrefix", this.streamPrefix);
          data.append("userIdNo", this.userId);

          // Add date range parameters
          data.append("dateRange", this.dateRange.type);
          if (this.dateRange.type === 'custom') {
            data.append("startDate", this.dateRange.startDate);
            data.append("endDate", this.dateRange.endDate);
          }

          console.log('Making API call with:', {
            userIdNo: this.userId,
            incomeTypePrefix: this.streamPrefix,
            dateRange: this.dateRange.type,
            startDate: this.dateRange.startDate,
            endDate: this.dateRange.endDate
          });

          const response = await execute(data, biller);

          console.log('API response:', response);

          if (response.data.success) {
            const result = response.data.data.userTransactionDetails;

            this.userDetails = result;
            this.allTransactions = result.userTransactions || [];
            this.filteredTransactions = [...this.allTransactions];

            console.log('User details set:', this.userDetails);

            this.$nextTick(() => {
              this.renderCharts();
            });
          } else {
            console.error('API call failed:', response.data);
            // Fallback to the original API and construct data
            await this.fetchUserDetailsFromOriginalAPI();
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
          // Fallback to the original API and construct data
          await this.fetchUserDetailsFromOriginalAPI();
        } finally {
          this.loading = false;
        }
      }, 100);
    },

    async fetchUserDetailsFromOriginalAPI() {
      try {
        const data = new FormData();
        data.append("function", "getMarketsRevenuePerformance");
        data.append("incomeTypePrefix", this.streamPrefix);
        data.append("userIdNo", this.userId);

        // Add date range parameters
        data.append("dateRange", this.dateRange.type);
        if (this.dateRange.type === 'custom') {
          data.append("startDate", this.dateRange.startDate);
          data.append("endDate", this.dateRange.endDate);
        }

        data.append("individualMetrics", "true");
        data.append("detailed", "true");

        const response = await execute(data, biller);

        if (response.data.success) {
          const result = response.data.data.marketsRevenuePerformance;

          if (result.individualUserMetrics &&
              (Array.isArray(result.individualUserMetrics) ? result.individualUserMetrics.length > 0 : true)) {

            const individualMetrics = Array.isArray(result.individualUserMetrics)
                ? result.individualUserMetrics[0]
                : result.individualUserMetrics;

            // Enhance with transaction details
            const enhancedDetails = await this.enhanceWithTransactionDetails(individualMetrics);
            this.userDetails = enhancedDetails;

            this.$nextTick(() => {
              this.renderCharts();
            });
          } else {
            console.error('User not found in response data');
            this.$toast?.error(`User details not found for ${this.getStreamName()}`);
            this.closeModal();
          }
        }
      } catch (error) {
        console.error("Error in fallback fetch:", error);
        if (this.$toast) {
          this.$toast.error(`Failed to load user details for ${this.getStreamName()}`);
        }
      }
    },

    async enhanceWithTransactionDetails(userMetrics) {
      // Fetch actual transactions for this user
      try {
        const data = new FormData();
        data.append("function", "getUserTransactions");
        data.append("incomeTypePrefix", this.streamPrefix);
        data.append("userIdNo", this.userId);

        // Add date range parameters
        data.append("dateRange", this.dateRange.type);
        if (this.dateRange.type === 'custom') {
          data.append("startDate", this.dateRange.startDate);
          data.append("endDate", this.dateRange.endDate);
        }

        const response = await execute(data, biller);

        if (response.data.success && response.data.data.userTransactions) {
          this.allTransactions = response.data.data.userTransactions;
          this.filteredTransactions = [...this.allTransactions];
        } else {
          // Generate mock transactions if API doesn't exist
          this.allTransactions = this.generateMockTransactions(userMetrics);
          this.filteredTransactions = [...this.allTransactions];
        }
      } catch (error) {
        console.log('User transactions API not available, generating mock data');
        this.allTransactions = this.generateMockTransactions(userMetrics);
        this.filteredTransactions = [...this.allTransactions];
      }

      return userMetrics;
    },

    generateMockTransactions(userMetrics) {
      const transactions = [];
      const categories = Object.keys(userMetrics.transactionBreakdown || {});

      if (categories.length === 0) {
        categories.push('General Payment');
      }

      // Generate transactions based on the performance data
      const totalTransactions = userMetrics.performanceMetrics?.currentMonth?.transactions || 10;
      const totalRevenue = userMetrics.performanceMetrics?.currentMonth?.revenue || 0;
      const avgAmount = totalRevenue / totalTransactions;

      for (let i = 0; i < Math.min(totalTransactions, 50); i++) {
        const date = new Date();
        date.setDate(date.getDate() - Math.floor(Math.random() * 30));

        const category = categories[Math.floor(Math.random() * categories.length)];
        const amount = Math.round(avgAmount * (0.5 + Math.random()));

        transactions.push({
          id: i + 1,
          date: date.toISOString(),
          category: category,
          amount: amount,
          receiptNumber: `${this.streamPrefix}${10000 + i}`,
          zone: userMetrics.userInfo?.zone || 'Unknown'
        });
      }

      return transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    filterTransactions() {
      let filtered = [...this.allTransactions];

      // Date filter
      if (this.transactionFilters.date) {
        filtered = filtered.filter(t =>
            t.date.startsWith(this.transactionFilters.date)
        );
      }

      // Category filter
      if (this.transactionFilters.category) {
        filtered = filtered.filter(t =>
            t.category === this.transactionFilters.category
        );
      }

      // Amount range filter
      if (this.transactionFilters.amountRange) {
        if (this.transactionFilters.amountRange === 'low') {
          filtered = filtered.filter(t => t.amount < 500);
        } else if (this.transactionFilters.amountRange === 'medium') {
          filtered = filtered.filter(t => t.amount >= 500 && t.amount <= 2000);
        } else if (this.transactionFilters.amountRange === 'high') {
          filtered = filtered.filter(t => t.amount > 2000);
        }
      }

      this.filteredTransactions = filtered;
      this.transactionsCurrentPage = 1;
    },

    refreshTransactions() {
      console.log('Refreshing transactions...');
      this.fetchUserDetails();
    },

    exportTransactions() {
      let csv = `User Transactions Report - ${this.getStreamName()}\n`;
      csv += `User: ${this.userDetails.userInfo.names}\n`;
      csv += `ID: ${this.userDetails.userInfo.idNo}\n`;
      csv += `Zone: ${this.userDetails.userInfo.zone}\n`;
      csv += `Stream: ${this.streamPrefix}\n`;
      csv += `Period: ${this.getDateRangeLabel()}\n`;
      csv += `Generated: ${new Date().toLocaleString()}\n\n`;

      csv += 'Date,Time,Category,Amount,Receipt,Zone\n';

      this.filteredTransactions.forEach(transaction => {
        csv += `${this.formatDateTime(transaction.date)},${this.formatTime(transaction.date)},"${transaction.category}",${transaction.amount},"${transaction.receiptNumber}","${transaction.zone || 'N/A'}"\n`;
      });

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.streamPrefix}_user_transactions_${this.userDetails.userInfo.idNo}_${this.dateRange.type}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    },

    renderCharts() {
      if (!this.userDetails) return;

      console.log('Rendering charts...');

      // Add a delay to ensure DOM is fully rendered
      this.$nextTick(() => {
        setTimeout(() => {
          // Monthly Trend Chart
          if (document.getElementById('monthlyTrendChart')) {
            this.renderMonthlyTrend();
          } else {
            console.log('Monthly trend chart container not found');
          }

          // Category Pie Chart
          if (this.$refs.categoryPieChart) {
            this.renderCategoryPie();
          } else {
            console.log('Category pie chart container not found');
          }
        }, 300); // Increased delay to ensure modal is fully rendered
      });
    },

    renderMonthlyTrend() {
      const months = this.userDetails.monthlyTrend.map(m => m.month.substring(0, 10));
      const revenues = this.userDetails.monthlyTrend.map(m => m.revenue);
      const transactions = this.userDetails.monthlyTrend.map(m => m.transactions);

      Highcharts.chart('monthlyTrendChart', {
        chart: { type: 'column' },
        title: { text: null },
        subtitle: { text: this.getDateRangeLabel() },
        xAxis: { categories: months },
        yAxis: [{
          title: { text: 'Revenue (KES)' },
          labels: {
            formatter: function() {
              return 'KES ' + Highcharts.numberFormat(this.value, 0);
            }
          }
        }, {
          title: { text: 'Transactions' },
          opposite: true
        }],
        tooltip: {
          shared: true
        },
        series: [{
          name: 'Revenue',
          data: revenues,
          color: '#10b981',
          yAxis: 0,
          tooltip: {
            valuePrefix: 'KES '
          }
        }, {
          name: 'Transactions',
          type: 'line',
          data: transactions,
          color: '#f59e0b',
          yAxis: 1
        }]
      });
    },

    renderCategoryPie() {
      console.log('renderCategoryPie called');

      if (!this.userDetails?.transactionBreakdown) {
        console.log('No transaction breakdown data available');
        return;
      }

      // Use the filtered validTransactionBreakdown data
      const data = Object.entries(this.validTransactionBreakdown)
          .filter(([category, values]) => values.amount > 0)
          .map(([category, values]) => ({
            name: category || 'Uncategorized',
            y: Number(values.amount)
          }));

      console.log('Pie chart data:', data);

      if (data.length === 0) {
        console.log('No data for pie chart');
        return;
      }

      // Use Vue ref instead of getElementById
      const container = this.$refs.categoryPieChart;
      if (!container) {
        console.log('Pie chart container ref not found');
        return;
      }

      // Clear any existing chart
      if (container.chart) {
        container.chart.destroy();
      }

      try {
        // Store chart instance on container
        container.chart = Highcharts.chart(container, {
          chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 280,
            animation: {
              duration: 1000
            }
          },
          title: {
            text: null
          },
          credits: {
            enabled: false
          },
          tooltip: {
            pointFormat: 'Revenue: <b>KES {point.y:,.0f}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
          },
          plotOptions: {
            pie: {
              allowPointSelect: true,
              cursor: 'pointer',
              dataLabels: {
                enabled: true,
                format: '{point.percentage:.0f}%',
                distance: -30,
                style: {
                  fontSize: '11px',
                  textOutline: '1px contrast',
                  color: 'white',
                  fontWeight: 'bold'
                }
              },
              showInLegend: false  // Disable legend to save space
            }
          },
          series: [{
            name: 'Revenue',
            colorByPoint: true,
            innerSize: '50%',
            data: data
          }]
        });

        console.log('Pie chart rendered successfully');

        // Force a reflow after a short delay
        setTimeout(() => {
          if (container.chart && container.chart.reflow) {
            container.chart.reflow();
          }
        }, 100);

      } catch (error) {
        console.error('Error rendering pie chart:', error);
      }
    },

    getCategoryColor(category) {
      const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f'];
      const validCategories = Object.keys(this.validTransactionBreakdown);
      const index = validCategories.indexOf(category);
      return colors[index % colors.length];
    },

    closeModal() {
      console.log('Closing modal');
      this.$emit('close');
      // Reset state when closing
      this.userDetails = null;
      this.lastFetchedUserId = null;
      this.allTransactions = [];
      this.filteredTransactions = [];
    },

    numberFormat(num) {
      return new Intl.NumberFormat('en-GB').format(num || 0);
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    },

    formatDateTime(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    exportUserReport() {
      if (!this.userDetails) return;

      let csv = `User Performance Report - ${this.getStreamName()}\n`;
      csv += `Generated: ${new Date().toLocaleString()}\n`;
      csv += `Stream: ${this.streamPrefix}\n`;
      csv += `Period: ${this.getDateRangeLabel()}\n\n`;

      // User Info
      csv += 'USER INFORMATION\n';
      csv += `Name,${this.userDetails.userInfo.names}\n`;
      csv += `ID Number,${this.userDetails.userInfo.idNo}\n`;
      csv += `Zone,${this.userDetails.userInfo.zone}\n`;
      csv += `Location,"${this.userDetails.userInfo.subCounty} - ${this.userDetails.userInfo.ward}"\n\n`;

      // Performance Summary
      csv += 'PERFORMANCE SUMMARY\n';
      csv += `Current Period Revenue,${this.userDetails.performanceMetrics.currentMonth.revenue}\n`;
      csv += `Previous Period Revenue,${this.userDetails.performanceMetrics.lastMonth.revenue}\n`;
      csv += `Total Revenue,${this.userDetails.performanceMetrics.yearToDate.revenue}\n`;
      csv += `Total Transactions,${this.userDetails.performanceMetrics.yearToDate.transactions}\n\n`;

      // Trend Data
      csv += 'PERFORMANCE TREND\n';
      csv += 'Period,Revenue,Transactions\n';
      this.userDetails.monthlyTrend.forEach(period => {
        csv += `${period.month},${period.revenue},${period.transactions}\n`;
      });
      csv += '\n';

      // Category Breakdown
      csv += 'CATEGORY BREAKDOWN\n';
      csv += 'Category,Amount,Count\n';
      Object.entries(this.validTransactionBreakdown).forEach(([category, values]) => {
        csv += `"${category || 'Uncategorized'}",${values.amount},${values.count}\n`;
      });
      csv += '\n';

      // Transactions
      csv += 'ALL TRANSACTIONS\n';
      csv += 'Date,Time,Category,Amount,Receipt,Zone\n';
      this.allTransactions.forEach(transaction => {
        csv += `${this.formatDateTime(transaction.date)},${this.formatTime(transaction.date)},"${transaction.category}",${transaction.amount},"${transaction.receiptNumber}","${transaction.zone || 'N/A'}"\n`;
      });

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.streamPrefix}_user_complete_report_${this.userDetails.userInfo.idNo}_${this.dateRange.type}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.5;
}

.avatar-lg {
  height: 5rem;
  width: 5rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

/* Custom scrollbar for tables */
.table-responsive {
  max-height: 400px;
  overflow-y: auto;
}

.table-responsive::-webkit-scrollbar {
  width: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Color box for category legend */
.color-box {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 8px;
  display: inline-block;
}

/* Ensure chart container has proper dimensions */
#categoryPieChart {
  position: relative;
  width: 100% !important;
  height: 300px !important;
  min-height: 300px !important;
}

/* Sticky header for transactions table */
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Hover effects */
.table tbody tr:hover {
  background-color: #f5f5f5;
}
</style>